import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { dateTime } from "@/lib/format";

export default function AccountSecurity() {
  const [cur, setCur] = useState("");
  const [nxt, setNxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [prq, setPrq] = useState({ type: "export", note: "" });
  const [prqBusy, setPrqBusy] = useState(false);
  const { data: priv, reload } = useFetch("/account/privacy-requests");
  const submitPwd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/password", { current_password: cur, new_password: nxt });
      toast.success("Password updated. Other sessions were signed out.");
      setCur("");
      setNxt("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const submitPrq = async (e) => {
    e.preventDefault();
    setPrqBusy(true);
    try {
      const r = await api.post("/account/privacy-requests", prq);
      toast.success(`Request received — reference ${r.data.reference}`);
      setPrq({ type: "export", note: "" });
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPrqBusy(false);
    }
  };
  const requests = priv?.items || [];
  return (
    <div className="space-y-10 max-w-xl" data-testid="account-security">
      <section>
        <h2 className="font-display text-3xl">Change password</h2>
        <form onSubmit={submitPwd} className="mt-6 space-y-5 bg-white border rounded-lg p-6 lg:p-8" data-testid="security-password-form">
          <div><Label htmlFor="s-cur">Current password</Label><Input id="s-cur" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="security-current-input" /></div>
          <div><Label htmlFor="s-new">New password</Label><Input id="s-new" type="password" required value={nxt} onChange={(e) => setNxt(e.target.value)} data-testid="security-new-input" /><p className="text-xs text-muted-foreground mt-1.5">Minimum 10 characters with letters and numbers. We'll sign out other sessions.</p></div>
          <button type="submit" disabled={busy || !cur || !nxt} className="btn-ink" data-testid="security-password-submit">{busy ? "Updating…" : "Update password"}</button>
        </form>
      </section>
      <section>
        <h2 className="font-display text-3xl">Privacy requests</h2>
        <p className="text-sm text-muted-foreground mt-2">Submit a request to export, rectify or erase your data. We respond within 30 days.</p>
        <form onSubmit={submitPrq} className="mt-6 space-y-5 bg-white border rounded-lg p-6 lg:p-8" data-testid="security-privacy-form">
          <div><Label>Type of request</Label>
            <Select value={prq.type} onValueChange={(v) => setPrq({ ...prq, type: v })}>
              <SelectTrigger className="bg-white" data-testid="privacy-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="export">Data export</SelectItem><SelectItem value="erasure">Account erasure</SelectItem><SelectItem value="rectification">Rectification</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="prq-note">Note (optional)</Label><Textarea id="prq-note" rows={4} value={prq.note} onChange={(e) => setPrq({ ...prq, note: e.target.value })} data-testid="privacy-note-input" /></div>
          <button type="submit" disabled={prqBusy} className="btn-ink" data-testid="privacy-submit-button">{prqBusy ? "Submitting…" : "Submit request"}</button>
        </form>
        {requests.length > 0 && (
          <ul className="mt-6 border-t pt-4 space-y-3" data-testid="privacy-requests-list">
            {requests.map((r) => <li key={r.id} className="flex justify-between text-sm" data-testid={`privacy-request-${r.id}`}><span>{r.type} · {dateTime(r.created_at)}</span><span className="text-muted-foreground capitalize">{r.status}</span></li>)}
          </ul>
        )}
      </section>
    </div>
  );
}
